/**
 * useFormSubmission Hook
 * Shared form submission logic for NCR, IPI, and Customer Complaint forms
 */

import { useState, useRef, useEffect } from 'react';
import { ENABLE_EPICOR_SUBMISSION, API_ENDPOINTS, GRAPHQL_CONFIG } from '../constants/fusionManageConstants';
import { CREATE_NCR_MUTATION } from '../../timesheet/queries';
import {
  extractSelectValue,
  extractSelectLabel,
  normalizeSelectField,
  prepareLocationFields,
  prepareRelatedSupplier,
  prepareInternalOrExternal,
  extractProductName,
  prepareCostFields,
  prepareAllAssignmentFields,
  formatDateForFusion
} from '../utils/formDataTransformers';

/**
 * Custom hook for form submission
 * @param {string} formType - Form type: 'NCR', 'IPI', or 'CustomerComplaint'
 * @param {Function} getMissingRequiredFields - Function to validate required fields
 * @param {Object} initialState - Initial form state for reset
 * @param {Function} onFormSubmitSuccess - Success callback
 * @param {Function} onFormSubmitError - Error callback
 * @param {Function} generateAutoFields - Optional function to regenerate auto fields (for IPI)
 * @returns {Object} - Submission state and handlers
 */
export const useFormSubmission = (
  formType,
  getMissingRequiredFields,
  initialState,
  onFormSubmitSuccess,
  onFormSubmitError,
  generateAutoFields = null
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [hdCaseNum, setHdCaseNum] = useState(null);
  const [fusionManageStatus, setFusionManageStatus] = useState(null);
  const [fusionManageMessage, setFusionManageMessage] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [missingRequiredFields, setMissingRequiredFields] = useState([]);
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Prepare submission data based on form type
   * @param {Object} formData - Form data object
   * @returns {Object} - Prepared submission data
   */
  const prepareSubmissionData = (formData) => {
    const incidentDateISO = formatDateForFusion(formData.IncidentDate);
    const raisedDateISO = formatDateForFusion(formData.RaisedDate);

    // Common fields for all forms
    const baseSubmissionData = {
      // Map new fields to legacy schema fields
      CompanyID: formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned || '',
      ProjectID: formData.ProjectID?.value || formData.ProjectID,
      JobCode: formData.JobNumber?.value || formData.JobNumber,
      SourceTypeID: formData.SourceTypeID || '',
      SourceType: formData.SourceType || '',
      CaseTitle: formData.NCRTitle || formData.CaseTitle,
      CaseSummary: formData.Description || formData.CaseSummary,
      Market: extractSelectLabel(formData.Market),
      SupplierNumber: formData.SupplierNumber || '',
      SupplierName: formData.SupplierName || '',
      IncidentDate: incidentDateISO,
      IncidentLocation: formData.IncidentLocation || '',
      OtherLocation: formData.OtherLocation || '',
      IncidentArea: formData.IncidentArea || '',
      NCRChampionID: formData.NCRChampionAssigned?.value || formData.NCRChampionAssigned || formData.NCRChampionID,
      NCRChampion: formData.NCRChampion || '',
      CaseTypeID: formData.CaseTypeID || '',
      CaseType: formData.CaseType || '',
      ProjectRelated: formData.ProjectRelated !== undefined ? formData.ProjectRelated : true,
      SafetyCritical: formData.SafetyCritical !== undefined ? formData.SafetyCritical : false,
      LegalCompliance: formData.LegalCompliance !== undefined ? formData.LegalCompliance : false,
      NCRReason: formData.NCRReason || '',
      NCRInitiator: formData.NCRInitiator || '',
      Notes: formData.Notes || '',
      NCRReasonID: formData.NCRReasonID || '',
      NCRInitiatorID: formData.RaisedBy || formData.NCRInitiatorID
    };

    // Form-specific data preparation
    let formSpecificData = {};

    if (formType === 'IPI') {
      // IPI-specific fields
      formSpecificData = {
        ...formData,
        CaseTitle: formData.NCRTitle || `IPI ${formData.Number}: ${formData.DefectType} - ${formData.PartNumber}`,
        CaseSummary: formData.Description,
        ProjectName: formData.ProjectName?.value || formData.ProjectName || '',
        JobCode: formData.JobNumber?.value || formData.JobNumber || formData.JobCode,
        NCRChampionID: null,
        NCRInitiatorID: formData.RaisedBy,
        ProjectOrTechnology: formData.ProjectOrTechnology?.value
          ? { value: formData.ProjectOrTechnology.value, label: formData.ProjectOrTechnology.label || formData.ProjectOrTechnology.value }
          : (formData.ProjectOrTechnology || null),
        RelatedSupplier: formData.RelatedSupplier || [],
        CostOfNCR: formData.CostOfNCR || 0,
        DrawingNumber: formData.PartNumber || '',
        // IPI-specific fields with value/label extraction
        DefectType: formData.DefectType?.value
          ? (() => {
              const cleanValue = (str) => {
                if (!str || typeof str !== 'string') return str;
                return str.trim().replace(/\s+-\s*$/, '').trim();
              };
              const cleanedValue = cleanValue(formData.DefectType.value);
              const cleanedLabel = cleanValue(formData.DefectType.label || formData.DefectType.value);
              return { value: cleanedValue, label: cleanedLabel };
            })()
          : formData.DefectType,
        PartNumber: formData.PartNumber,
        AffectedQuantity: formData.AffectedQuantity,
        ClockingCode: normalizeSelectField(formData.ClockingCode),
        IsReworkRequired: normalizeSelectField(formData.IsReworkRequired),
        AffectedProduct: normalizeSelectField(formData.AffectedProduct),
        IsPCBRelated: normalizeSelectField(formData.IsPCBRelated),
        PCBSerialNumber: formData.PCBSerialNumber,
        IssueType: normalizeSelectField(formData.IssueType),
        NameAndSurname: formData.RaisedBy,
        IPIType: formData.IPIType
      };
    } else {
      // NCR and Customer Complaint common fields
      formSpecificData = {
        ProjectOrTechnology: extractSelectLabel(formData.ProjectOrTechnology),
        Market: extractSelectLabel(formData.Market),
        NCRTitle: formData.NCRTitle,
        Description: formData.Description,
        InternalOrExternal: prepareInternalOrExternal(formData.InternalOrExternal),
        RelatedSupplier: prepareRelatedSupplier(formData.RelatedSupplier, formType === 'NCR' ? 'NCRForm' : 'CustomerComplaintForm'),
        ...prepareCostFields(formData),
        ProductName: extractProductName(formData.ProductName),
        ProductVariant: formData.ProductVariant?.value || formData.ProductVariant,
        DrawingNumber: formData.DrawingNumber,
        SoftwareVersion: formData.SoftwareVersion,
        ProductSerialNumber: formData.ProductSerialNumber,
        AffectedQuantity: formData.AffectedQuantity ? parseFloat(formData.AffectedQuantity) : null,
        ProjectName: formData.ProjectName?.value || formData.ProjectName || ''
      };
    }

    // Combine all data
    const completeFormData = {
      ...baseSubmissionData,
      ...formSpecificData,
      ...prepareAllAssignmentFields(formData),
      IncidentDate: incidentDateISO,
      RaisedDate: raisedDateISO
    };

    return { submissionData: baseSubmissionData, completeFormData };
  };

  /**
   * Handle form submission
   * @param {Object} formData - Form data to submit
   */
  const handleSubmit = async (formData) => {
    setHasInteracted(true);
    setValidationAttempted(true);

    // Validate form before submission
    const validationErrors = getMissingRequiredFields(formData);
    setMissingRequiredFields(validationErrors);

    if (validationErrors.length > 0) {
      setErrorMessage(`Please complete the required fields: ${validationErrors.join(', ')}`);
      setShowErrorAlert(true);
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setShowErrorAlert(false), 5000);
      return;
    }

    setIsSubmitting(true);
    try {
      const { submissionData, completeFormData } = prepareSubmissionData(formData);

      let response;
      const endpoint = formType === 'NCR' 
        ? API_ENDPOINTS.NCR 
        : formType === 'IPI' 
        ? API_ENDPOINTS.IPI 
        : API_ENDPOINTS.CUSTOMER_COMPLAINT;

      if (ENABLE_EPICOR_SUBMISSION && formType === 'NCR') {
        // GraphQL submission (only for NCR when Epicor is enabled)
        console.log(`[${formType}Form] Submitting to BOTH Epicor AND Fusion Manage via GraphQL...`);
        console.log(`[${formType}Form] Payload:`, JSON.stringify(completeFormData, null, 2));
        
        response = await fetch(API_ENDPOINTS.GRAPHQL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: CREATE_NCR_MUTATION,
            variables: {
              company: submissionData.CompanyID || '',
              efxLibraryID: GRAPHQL_CONFIG.EFX_LIBRARY_IDS.NCR,
              efxFunctionID: GRAPHQL_CONFIG.EFX_FUNCTION_IDS.CREATE_NCR,
              params: completeFormData
            }
          })
        });
      } else {
        // REST API submission (Fusion Manage only)
        console.log(`[${formType}Form] Submitting to Fusion Manage ONLY via REST API...`);
        console.log(`[${formType}Form] Payload:`, JSON.stringify(completeFormData, null, 2));
        
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(completeFormData)
        });
      }

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      const jsonResponse = await response.json();

      let formNumber, fusionStatus, fusionMessage;

      if (ENABLE_EPICOR_SUBMISSION && formType === 'NCR') {
        // GraphQL response format
        if (jsonResponse.errors) {
          const errorMessage = jsonResponse.errors.map(e => e.message).join(', ');
          throw new Error(errorMessage);
        }

        if (!jsonResponse.data || !jsonResponse.data.createNCR) {
          throw new Error('Invalid response from server');
        }

        const ncrData = jsonResponse.data.createNCR;
        formNumber = ncrData.HDCaseNum;
        fusionStatus = ncrData.fusionManageStatus;
        fusionMessage = ncrData.fusionManageMessage || '';
      } else {
        // REST API response format
        if (!jsonResponse.success) {
          let errorMessage = jsonResponse.message || jsonResponse.error || `${formType} creation failed`;
          if (jsonResponse.fusionManageValidationErrors && jsonResponse.fusionManageValidationErrors.length > 0) {
            errorMessage += ': ' + jsonResponse.fusionManageValidationErrors.join(', ');
          }
          throw new Error(errorMessage);
        }

        formNumber = jsonResponse.Number;
        fusionStatus = jsonResponse.fusionManageStatus || 'success';
        fusionMessage = jsonResponse.fusionManageMessage || 'Successfully created in Fusion Manage';
      }

      setHdCaseNum(formNumber);

      // Handle Fusion Manage status
      if (fusionStatus) {
        setFusionManageStatus(fusionStatus);
        setFusionManageMessage(fusionMessage);

        // Show warning if Fusion Manage sync failed (only relevant when Epicor enabled)
        if (ENABLE_EPICOR_SUBMISSION && formType === 'NCR' && (fusionStatus === 'failed' || fusionStatus === 'error')) {
          console.warn(`${formType} ${formNumber} created in Epicor, but Fusion Manage sync failed: ${fusionMessage}`);
        }
      }

      // Reset form
      const resetData = {
        ...initialState,
        RaisedDate: new Date().toISOString().split('T')[0]
      };
      
      // IPI form has generateAutoFields function
      if (generateAutoFields && formType === 'IPI') {
        // For IPI, reset happens after generateAutoFields
        onFormSubmitSuccess(formNumber);
        // Note: Form reset and generateAutoFields should be handled by the component
      } else {
        // For NCR and Customer Complaint, reset immediately
        setHasInteracted(false);
        onFormSubmitSuccess(formNumber);
      }

    } catch (error) {
      console.error('Error:', error);
      const errorMsg = error.message || 'An error occurred while submitting the form.';
      setErrorMessage(errorMsg);
      setShowErrorAlert(true);
      onFormSubmitError(errorMsg);
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setShowErrorAlert(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    isSubmitting,
    errorMessage,
    showErrorAlert,
    setShowErrorAlert,
    hdCaseNum,
    setHdCaseNum,
    fusionManageStatus,
    fusionManageMessage,
    hasInteracted,
    setHasInteracted,
    validationAttempted,
    setValidationAttempted,
    missingRequiredFields,
    setMissingRequiredFields
  };
};

